import React, { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { apiUrl } from '../../config/api'
import ChessBoard from '../Chess/ChessBoard'
import { showSuccess, showError } from '../Toast/Toast'
import soundManager from '../../utils/soundUtils'
import './Replay.css'

export default function Replay({ gameId, moveNumber = 0, onBack, gameData: importedGameData }) {
  const { user } = useAuth()
  const [gameData, setGameData] = useState(importedGameData)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Replay state
  const [currentMoveIndex, setCurrentMoveIndex] = useState(moveNumber)
  const [isPlaying, setIsPlaying] = useState(false)
  const [playbackSpeed, setPlaybackSpeed] = useState(1) // 0.5x, 1x, 2x, 4x
  const [showSpecialSquares, setShowSpecialSquares] = useState(true)
  const [showAnalysis, setShowAnalysis] = useState(false)

  // Load game data
  useEffect(() => {
    if (importedGameData) {
      // Skip loading if we have imported data
      setLoading(false)
    } else if (gameId) {
      loadGameData()
    }
  }, [gameId])

  // Auto-play functionality
  useEffect(() => {
    if (!isPlaying || !gameData || currentMoveIndex >= gameData.moves.length) {
      setIsPlaying(false)
      return
    }

    const timeout = setTimeout(() => {
      setCurrentMoveIndex(prev => {
        const nextIndex = Math.min(prev + 1, gameData.moves.length)
        // Play sound if we're advancing to a new move during auto-play
        if (nextIndex > prev && nextIndex <= gameData.moves.length) {
          // Check if the move we're advancing to was a capture
          const move = gameData.moves[nextIndex - 1]
          const isCapture = move && move.captured && move.captured !== ''
          soundManager.playMoveOrCapture(isCapture)
        }
        return nextIndex
      })
    }, (1000 / playbackSpeed))

    return () => clearTimeout(timeout)
  }, [isPlaying, currentMoveIndex, gameData, playbackSpeed])

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!gameData) return

      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault()
          stepBackward()
          break
        case 'ArrowRight':
          event.preventDefault()
          stepForward()
          break
        case ' ':
          event.preventDefault()
          togglePlayback()
          break
        case 'Home':
          event.preventDefault()
          jumpToStart()
          break
        case 'End':
          event.preventDefault()
          jumpToEnd()
          break
        default:
          break
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [gameData, currentMoveIndex])

  const loadGameData = async () => {
    try {
      setLoading(true)
      setError(null)

      const response = await fetch(apiUrl(`games/${gameId}/replay`))
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Game not found')
        }
        throw new Error('Failed to load game data')
      }

      const data = await response.json()
      setGameData(data)

      // Set initial move position
      setCurrentMoveIndex(moveNumber)
    } catch (err) {
      console.error('Failed to load game data:', err)
      setError(err.message || 'Failed to load game data')
    } finally {
      setLoading(false)
    }
  }

  const stepForward = useCallback(() => {
    if (!gameData) return
    setCurrentMoveIndex(prev => {
      const nextIndex = Math.min(prev + 1, gameData.moves.length)
      // Play sound if we're advancing to a new move (not at the end)
      if (nextIndex > prev && nextIndex <= gameData.moves.length) {
        // Check if the move we're advancing to was a capture
        const move = gameData.moves[nextIndex - 1]
        const isCapture = move && move.captured && move.captured !== ''
        soundManager.playMoveOrCapture(isCapture)
      }
      return nextIndex
    })
    setIsPlaying(false)
  }, [gameData])

  const stepBackward = useCallback(() => {
    setCurrentMoveIndex(prev => Math.max(prev - 1, 0))
    setIsPlaying(false)
  }, [])

  const jumpToStart = useCallback(() => {
    setCurrentMoveIndex(0)
    setIsPlaying(false)
  }, [])

  const jumpToEnd = useCallback(() => {
    if (!gameData) return
    setCurrentMoveIndex(gameData.moves.length)
    setIsPlaying(false)
  }, [gameData])

  const jumpToMove = useCallback((moveIndex) => {
    if (!gameData) return
    setCurrentMoveIndex(Math.max(0, Math.min(moveIndex, gameData.moves.length)))
    setIsPlaying(false)
  }, [gameData])

  const togglePlayback = useCallback(() => {
    if (!gameData) return

    // If at the end, restart from beginning
    if (currentMoveIndex >= gameData.moves.length) {
      setCurrentMoveIndex(0)
      setIsPlaying(true)
    } else {
      setIsPlaying(prev => !prev)
    }
  }, [gameData, currentMoveIndex])

  const exportGame = async (format = 'pgn') => {
    try {
      const response = await fetch(apiUrl(`games/${gameId}/export?format=${format}`))
      if (!response.ok) {
        throw new Error('Failed to export game')
      }

      // Trigger download
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `game_${gameId.slice(0, 8)}.${format}`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Failed to export game:', err)
      alert('Failed to export game. Please try again.')
    }
  }

  const createGameJSON = () => {
    const exportData = {
      version: "1.0",
      format: "riftchess",
      game: {
        id: gameData.id,
        white_player: gameData.white_player,
        black_player: gameData.black_player,
        result: gameData.result,
        time_control: gameData.time_control,
        rated: gameData.rated,
        white_rating_before: gameData.white_rating_before,
        black_rating_before: gameData.black_rating_before,
        white_rating_after: gameData.white_rating_after,
        black_rating_after: gameData.black_rating_after,
        started_at: gameData.started_at,
        completed_at: gameData.completed_at,
      },
      initial_board: "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1",
      moves: gameData.moves.map(move => ({
        move_num: move.move_num,
        from: move.from,
        to: move.to,
        piece: move.piece,
        captured: move.captured,
        promotion: move.promotion,
        san: move.san,
        uci: move.uci,
        fen: move.fen,
        time_spent: move.time_spent,
        time_left: move.time_left,
        timestamp: move.timestamp,
        red_squares: move.red_squares,
        blue_squares: move.blue_squares,
        green_squares: move.green_squares,
        visual_effects: move.visual_effects,
        player_id: move.player_id
      })),
      final_state: {
        board: gameData.board,
        red_squares: gameData.red_squares,
        blue_squares: gameData.blue_squares,
        green_squares: gameData.green_squares,
        visual_effects: gameData.visual_effects
      }
    }
    return JSON.stringify(exportData, null, 2)
  }

  const exportGameJSON = () => {
    try {
      const jsonString = createGameJSON()
      const blob = new Blob([jsonString], { type: 'application/json' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `riftchess_game_${gameId.slice(0, 8)}.json`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Failed to export game JSON:', err)
      showError('Failed to export game. Please try again.')
    }
  }

  const copyGameJSON = async () => {
    try {
      const jsonString = createGameJSON()
      await navigator.clipboard.writeText(jsonString)
      showSuccess('Game JSON copied to clipboard!')
    } catch (err) {
      console.error('Failed to copy game JSON:', err)
      showError('Failed to copy to clipboard. Please try again.')
    }
  }

  const getCurrentBoardState = () => {
    if (!gameData || currentMoveIndex === 0) {
      return 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1' // Starting position
    }

    if (currentMoveIndex > gameData.moves.length) {
      return gameData.board // Final position
    }

    // Get FEN from the move at currentMoveIndex
    const moveIndex = currentMoveIndex - 1
    return gameData.moves[moveIndex]?.fen || gameData.board
  }

  const getCurrentVisualEffects = () => {
    if (!gameData || currentMoveIndex === 0) {
      return '[]'
    }

    if (currentMoveIndex > gameData.moves.length) {
      return gameData.visual_effects || '[]'
    }

    // Get visual effects from the NEXT move to show them 1 move earlier
    // This makes ghost effects appear before they actually trigger
    const nextMove = gameData.moves[currentMoveIndex]
    if (!nextMove) {
      // If there's no next move, use current move's effects
      const currentMove = gameData.moves[currentMoveIndex - 1]
      return currentMove?.visual_effects || '[]'
    }

    return nextMove?.visual_effects || '[]'
  }

  // Create a mock game object for ChessBoard component
  const getReplayGameForBoard = () => {
    if (!gameData) return null

    const currentBoardState = getCurrentBoardState()
    const specialSquares = getSpecialSquaresAtMove()

    return {
      id: gameData.id,
      board: currentBoardState,
      status: 'completed',
      red_squares: JSON.stringify(specialSquares.red),
      blue_squares: JSON.stringify(specialSquares.blue),
      green_squares: JSON.stringify(specialSquares.green),
      visual_effects: getCurrentVisualEffects()
    }
  }

  const getSpecialSquaresAtMove = () => {
    if (!showSpecialSquares || !gameData) {
      return { red: [], blue: [], green: [] }
    }

    // If at the start position, use empty squares
    if (currentMoveIndex === 0) {
      return { red: [], blue: [], green: [] }
    }

    // If beyond the last move, use the final game state
    if (currentMoveIndex > gameData.moves.length) {
      const red = gameData.red_squares && gameData.red_squares !== 'null' ?
        JSON.parse(gameData.red_squares || '[]') : []
      const blue = gameData.blue_squares && gameData.blue_squares !== 'null' ?
        JSON.parse(gameData.blue_squares || '[]') : []
      const green = gameData.green_squares && gameData.green_squares !== 'null' ?
        JSON.parse(gameData.green_squares || '[]') : []

      return {
        red: Array.isArray(red) ? red : [],
        blue: Array.isArray(blue) ? blue : [],
        green: Array.isArray(green) ? green : []
      }
    }

    // Get special squares from the NEXT move to show them 1 move earlier
    // This makes special squares visible before they actually spawn
    const nextMove = gameData.moves[currentMoveIndex]
    if (!nextMove) {
      // If there's no next move, check the current move's squares
      const currentMove = gameData.moves[currentMoveIndex - 1]
      if (!currentMove) {
        return { red: [], blue: [], green: [] }
      }

      const red = currentMove.red_squares && currentMove.red_squares !== 'null' ?
        JSON.parse(currentMove.red_squares || '[]') : []
      const blue = currentMove.blue_squares && currentMove.blue_squares !== 'null' ?
        JSON.parse(currentMove.blue_squares || '[]') : []
      const green = currentMove.green_squares && currentMove.green_squares !== 'null' ?
        JSON.parse(currentMove.green_squares || '[]') : []

      return {
        red: Array.isArray(red) ? red : [],
        blue: Array.isArray(blue) ? blue : [],
        green: Array.isArray(green) ? green : []
      }
    }

    const red = nextMove.red_squares && nextMove.red_squares !== 'null' ?
      JSON.parse(nextMove.red_squares || '[]') : []
    const blue = nextMove.blue_squares && nextMove.blue_squares !== 'null' ?
      JSON.parse(nextMove.blue_squares || '[]') : []
    const green = nextMove.green_squares && nextMove.green_squares !== 'null' ?
      JSON.parse(nextMove.green_squares || '[]') : []

    return {
      red: Array.isArray(red) ? red : [],
      blue: Array.isArray(blue) ? blue : [],
      green: Array.isArray(green) ? green : []
    }
  }

  const getSpecialSquaresForMove = (moveIndex) => {
    if (!gameData || moveIndex < 0 || moveIndex >= gameData.moves.length) {
      return { red: [], blue: [], green: [] }
    }

    const move = gameData.moves[moveIndex]
    if (!move) {
      return { red: [], blue: [], green: [] }
    }

    const red = move.red_squares && move.red_squares !== 'null' ?
      JSON.parse(move.red_squares || '[]') : []
    const blue = move.blue_squares && move.blue_squares !== 'null' ?
      JSON.parse(move.blue_squares || '[]') : []
    const green = move.green_squares && move.green_squares !== 'null' ?
      JSON.parse(move.green_squares || '[]') : []

    return {
      red: Array.isArray(red) ? red : [],
      blue: Array.isArray(blue) ? blue : [],
      green: Array.isArray(green) ? green : []
    }
  }

  if (loading) {
    return (
      <div className="replay-container">
        <div className="replay-header">
          <div className="replay-logo">
            <img
              src="/images/logo/finallogosquare.png"
              alt="RiftChess"
              className="logo-icon"
            />
          </div>
          <div className="game-matchup">
            <div className="players-vs">
              <span className="loading-text">Loading Game Replay...</span>
            </div>
          </div>
        </div>
        <div className="replay-loading">
          <div className="loading-spinner">Loading game data...</div>
        </div>
        <div className="replay-sidebar">
          <div className="move-list-section">
            <h3>Moves</h3>
            <div className="move-list">
              <p>Loading moves...</p>
            </div>
          </div>
          <div className="back-button-container">
            <button className="back-button" onClick={onBack}>
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="replay-container">
        <div className="replay-header">
          <div className="replay-logo">
            <img
              src="/images/logo/finallogosquare.png"
              alt="RiftChess"
              className="logo-icon"
            />
          </div>
          <div className="game-matchup">
            <div className="players-vs">
              <span className="error-text">Replay Error</span>
            </div>
          </div>
        </div>
        <div className="replay-error">
          <p>{error}</p>
          <button className="retry-button" onClick={loadGameData}>
            Try Again
          </button>
        </div>
        <div className="replay-sidebar">
          <div className="move-list-section">
            <h3>Moves</h3>
            <div className="move-list">
              <p>Unable to load moves</p>
            </div>
          </div>
          <div className="back-button-container">
            <button className="back-button" onClick={onBack}>
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (!gameData) return null

  const progress = gameData.moves.length > 0 ? (currentMoveIndex / gameData.moves.length) * 100 : 0

  return (
    <div className="replay-container">
      <div className="replay-header">
        <div className="replay-logo">
          <img
            src="/images/logo/finallogosquare.png"
            alt="RiftChess"
            className="logo-icon"
          />
        </div>
        <div className="game-matchup">
          <div className="players-vs-score">
            <span className="player white-player">
              {gameData.white_player?.username || 'White'} (white)
            </span>
            <span className="score-display">
              <span className="score-box white-score">
                {gameData.result === 'white_win' ? '1' : gameData.result === 'draw' ? '½' : '0'}
              </span>
              <span className="vs-text">vs</span>
              <span className="score-box black-score">
                {gameData.result === 'black_win' ? '1' : gameData.result === 'draw' ? '½' : '0'}
              </span>
            </span>
            <span className="player black-player">
              {gameData.black_player?.username || 'Black'} (black)
            </span>
          </div>
        </div>
      </div>

      <div className="replay-content">
        <div className="replay-board-section">
          {getReplayGameForBoard() && (
            <ChessBoard
              game={getReplayGameForBoard()}
              onMove={() => {}} // Disable moves in replay mode
              onSquareClick={() => {}} // Disable interactions in replay mode
              selectedSquare={null}
              legalMoves={[]}
              isPlayerTurn={false}
              orientation="white"
              boardTheme="riftchess"
              teleportTargets={[]}
            />
          )}
        </div>

        <div className="replay-sidebar">
          <div className="move-list-section">
            <h3>Moves</h3>
            <p className="moves-info">Special squares shown one move early to match live gameplay timing</p>
            <div className="move-list">
              {gameData.moves.map((move, index) => {
                // Show special squares from the NEXT move (1 move earlier display)
                const squares = index < gameData.moves.length - 1 ? getSpecialSquaresForMove(index + 1) : { red: [], blue: [], green: [] }
                return (
                  <div
                    key={`move-${index}-${move.san || move.id || index}`}
                    className={`move-item ${index + 1 === currentMoveIndex ? 'active' : ''}`}
                    onClick={() => jumpToMove(index + 1)}
                  >
                    <span className="move-number">
                      {index + 1}.
                    </span>
                    <span className="move-notation">{move.san}</span>
                    <div className="move-squares">
                      {squares.red.map((_, i) => (
                        <div key={`red-${i}`} className="square-indicator red"></div>
                      ))}
                      {squares.blue.map((_, i) => (
                        <div key={`blue-${i}`} className="square-indicator blue"></div>
                      ))}
                      {squares.green.map((_, i) => (
                        <div key={`green-${i}`} className="square-indicator green"></div>
                      ))}
                    </div>
                    {move.time_left > 0 && (
                      <span className="move-time">
                        {Math.floor(move.time_left / 60)}:{(move.time_left % 60).toString().padStart(2, '0')}
                      </span>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
          <div className="playback-controls">
            <button className="control-button" onClick={jumpToStart} title="First move">
              ⏮
            </button>
            <button className="control-button" onClick={stepBackward} title="Previous move">
              ‹
            </button>
            <button className="control-button play-pause" onClick={togglePlayback} title={isPlaying ? "Pause" : "Play"}>
              {isPlaying ? '⏸' : '▶'}
            </button>
            <button className="control-button" onClick={stepForward} title="Next move">
              ›
            </button>
            <button className="control-button" onClick={jumpToEnd} title="Last move">
              ⏭
            </button>
          </div>
          <div className="export-section">
            <button className="export-button" onClick={copyGameJSON}>
              Copy Game JSON
            </button>
            <button className="export-button" onClick={exportGameJSON}>
              Download Game (JSON)
            </button>
          </div>
          <div className="back-button-container">
            <button className="back-button" onClick={onBack}>
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>


      {showAnalysis && (
        <div className="analysis-section">
          <h3>Analysis</h3>
          {currentMoveIndex > 0 && gameData.moves[currentMoveIndex - 1] && (
            <div className="move-analysis">
              <p>Move: {gameData.moves[currentMoveIndex - 1].san}</p>
              <p>Time spent: {gameData.moves[currentMoveIndex - 1].time_spent}ms</p>
              <p>Accuracy: {gameData.moves[currentMoveIndex - 1].accuracy}%</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}